<template>
    <OpenableMenu>
        <template v-slot:label>
            Rooster opties
        </template>
        <template v-slot:content>
            <List>
                <ListItem v-if="canSetRosterInEditMode">
                    <div
                        @click="deployStampsUntil"
                    >
                        Medewerkers op rooster in bewerking zetten t/m {{ formattedDayUntilWhichToPerformRosterActions }}
                    </div>
                </ListItem>
                <ListItem v-if="canProposeRoster">
                    <div
                        @click="proposeRosterUntil(roster.rosterId, dayUntilWhichToPerformRosterActions)"
                    >
                        Rooster voorstellen t/m {{ formattedDayUntilWhichToPerformRosterActions }}
                    </div>
                </ListItem>
                <ListItem v-if="canPublishOrDeclineProposedRoster">
                    <div
                        @click="declineProposedRoster(roster.rosterId)"
                    >
                        Voorgesteld rooster afwijzen vanaf {{ formattedRejectProposalFromDate }}
                    </div>
                </ListItem>
                <ListItem v-if="canPublishOrDeclineProposedRoster">
                    <div
                        @click="publishRosterUntil(roster.rosterId, dayUntilWhichToPerformRosterActions)"
                    >
                        Rooster publiceren t/m {{ formattedDayUntilWhichToPerformRosterActions }}
                    </div>
                </ListItem>
                <ListItem v-if="canSetDeriveRightsUntil">
                    <div
                        @click="setDeriveRightsUntil(roster.rosterId, nextDerivedRightsUntilDate)"
                    >
                        Rooster definitief maken t/m {{ formattedNextDerivedRightsUntilDate }}
                    </div>
                </ListItem>
                <ListItem v-if="canMakeRosterVisible">
                    <div
                        @click="unpublishedPartVisibleUntil(roster.rosterId, dayUntilWhichToPerformRosterActions)"
                    >
                        Niet gepubliceerd rooster tonen t/m {{ formattedDayUntilWhichToPerformRosterActions }}
                    </div>
                </ListItem>
                <ListItem v-if="canUndoRosterVisible">
                    <div
                        @click="undoUnpublishedPartVisibleUntil(roster.rosterId, roster.proposedUntil)"
                    >
                        Niet gepubliceerd rooster tonen ongedaan maken
                    </div>
                </ListItem>
                <ListItem v-if="roster.wishInputEnabled && canCloseWishInput">
                    <div
                        @click="closeWishInputUntil(roster.rosterId, dayUntilWhichToPerformRosterActions)"
                    >
                        Wensinvoer sluiten t/m {{ formattedDayUntilWhichToPerformRosterActions }}
                    </div>
                </ListItem>
                <ListItem v-if="canChangeRosterTitles">
                    <div @click="addNewRosterTitle">Voeg roostertitel toe</div>
                </ListItem>
                <ListItem v-if="canEditRosterRankSets">
                    <div @click="openRosterRankSetsEditor">Medewerkervolgordes per datum wijzigen</div>
                </ListItem>
                <ListItem>
                    <a class="list-item-link" :href="roosterplanningHostUrl + '?pageid=P_ROSTER_COLLECTIVEPUBLISH&rosterid=' + roster.rosterId + '&weeksshown=' + clampedShownWeeks + '&begindate=' + startDate + '&referrerpage=P_ROOSTERWEB_COMP_OVV'" target="_blank">{{ publishAllOpenShiftsLabel }}</a>
                </ListItem>
                <ListItem>
                    <a class="list-item-link" :href="roosterplanningHostUrl + '?pageid=P_ROOSTERWEB_OPENSHIFT_PUBLISH_HISTORY&rosterid=' + roster.rosterId + '&weeksshown=' + clampedShownWeeks + '&begindate=' + startDate" target="_blank">Open diensten publicatiehistorie</a>
                </ListItem>
            </List>
        </template>
    </OpenableMenu>
</template>

<script lang="ts">
    import {Component, Vue, Prop} from 'vue-facing-decorator';
    import OpenableMenu from '@/components/Branding/OpenableMenu.vue';
    import ListItem from '@/components/Branding/ListItem.vue';
    import List from '@/components/Branding/List.vue';
    import Dates from '@cquential/date-utils';
    import {ModalTheme} from '@/types/enums/ModalTheme';
    import {ApiClient, Title, Resource, Globals} from '@cquential/api';
    import StampDeployModal from '@/components/modals/StampDeployModal.vue';
    import TitleEditModal from '@/components/modals/TitleEditModal.vue';
    import RosterRankSetsModal from '@/components/modals/RosterRankSetsModal.vue';
    import Notify from '@/components/modals/Notify.vue';
    import {RosterData, RosterPublishConfirmData, PermissionsForRoster} from '@/types/types';
    import PublishRosterModal from '@/components/modals/PublishRosterModal.vue';
    import Loader from '@/components/modals/Loader.vue';
    import {UserPermissions} from '@/types/enums/UserPermissions';
    import GenericConfirmationModal from '@/components/modals/GenericConfirmationModal.vue';
    import GlobalErrorModal from '@/components/modals/GlobalErrorModal.vue';
    import RoosterplanningUrlBuilder from '@/util/RoosterplanningUrlBuilder';

    @Component({
        components: {
            OpenableMenu,
            ListItem,
            List,
        },
        emits: ['reload:roster', 'rosterProposed', 'rosterPublished'],
    })
    export default class RosterActionsMenu extends Vue {
        @Prop({required: true}) public globals!: Globals;
        @Prop({required: true}) public roster!: RosterData;
        @Prop({required: true}) public permissions!: PermissionsForRoster;
        @Prop({type: Boolean}) public allowRosterActionsToAnyWeekDay!: boolean;

        public get canProposeRoster(): boolean {
            return this.permissions[UserPermissions.ProposeRoster]
                && this.dayUntilWhichToPerformRosterActions !== null
                && (
                    (this.roster.proposedUntil !== undefined && this.dayUntilWhichToPerformRosterActions > this.roster.proposedUntil)
                    || (!this.roster.proposedUntil && this.roster.beginDate !== undefined)
                );
        }

        public get canPublishOrDeclineProposedRoster(): boolean {
            return this.permissions[UserPermissions.AcceptProposal]
                && this.dayUntilWhichToPerformRosterActions !== null
                && (!!this.roster.proposedUntil && this.dayUntilWhichToPerformRosterActions <= this.roster.proposedUntil)
                && (
                    (!!this.roster.publishedUntil && this.dayUntilWhichToPerformRosterActions > this.roster.publishedUntil)
                    || (!this.roster.publishedUntil && this.roster.beginDate !== undefined)
                );
        }

        public get canSetDeriveRightsUntil(): boolean {
            return this.globals.rosterHasDeriveRightsUntilEnabled
                && this.permissions[UserPermissions.SetRightsDerived]
                && !!this.roster.publishedUntil && !!this.roster.deriveRightsUntil
                && this.roster.publishedUntil > this.roster.deriveRightsUntil
                && this.roster.publishedUntil >= this.roster.days[0].date
                && this.roster.deriveRightsUntil < this.roster.days[this.roster.days.length - 1].date;
        }

        public get canSetRosterInEditMode(): boolean {
            if (!this.permissions[UserPermissions.ChangeStampsToConstruction] || !this.dayUntilWhichToPerformRosterActions) {
                return false;
            }

            for (const resource of this.roster.rosterResources) {
                if (!resource.stampDeployedUntil || resource.stampDeployedUntil < this.dayUntilWhichToPerformRosterActions) {
                    return true;
                }
            }

            return false;
        }

        public get canCloseWishInput(): boolean {
            return this.permissions[UserPermissions.WishInputClose] && this.roster.wishInputClosedUntil !== null && this.dayUntilWhichToPerformRosterActions !== null && this.dayUntilWhichToPerformRosterActions > this.roster.wishInputClosedUntil;
        }

        public get canMakeRosterVisible(): boolean {
            if (this.dayUntilWhichToPerformRosterActions === null) {
                return false;
            }

            return this.permissions[UserPermissions.ShowRoster] && (this.roster.unpublishedPartVisibleUntil === null || this.dayUntilWhichToPerformRosterActions > this.roster.unpublishedPartVisibleUntil);
        }

        public get canUndoRosterVisible(): boolean {
            return this.permissions[UserPermissions.ShowRoster] && (this.roster.unpublishedPartVisibleUntil !== null && this.roster.unpublishedPartVisibleUntil > this.roster.proposedUntil!);
        }

        public get canChangeRosterTitles(): boolean {
            return this.permissions[UserPermissions.ReorderUsers];
        }

        public get canEditRosterRankSets(): boolean {
            return this.permissions[UserPermissions.EditRosterRankSets];
        }

        public get dayUntilWhichToPerformRosterActions(): Date | null {
            return this.allowRosterActionsToAnyWeekDay ? this.lastShownDay : this.lastShownSunday;
        }

        public get firstShownDay(): Date | null {
            return this.roster.days.length > 0 ? this.roster.days[0].date : null;
        }

        public get lastShownDay(): Date | null {
            return this.roster.days.length > 0 ? this.roster.days.at(-1)!.date : null;
        }

        public get lastShownSunday(): Date | null {
            if (this.roster.days.length === 0) {
                return null;
            }

            const lastVisibleSunday: Date = new Date(this.roster.days[this.roster.days.length - 1].date);
            if (Dates.Utils.getDay(lastVisibleSunday) !== Dates.Utils.SUNDAY) {
                lastVisibleSunday.setDate(lastVisibleSunday.getDate() - Dates.Utils.getDay(lastVisibleSunday));
            }

            if (lastVisibleSunday < this.roster.days[0].date) {
                return null;
            }

            return lastVisibleSunday;
        }

        public get nextDerivedRightsUntilDate(): Date | null {
            if (!this.roster.publishedUntil) {
                return null;
            }
            const day: Date | null = this.dayUntilWhichToPerformRosterActions;
            if (day && day < this.roster.publishedUntil) {
                return day;
            }

            return this.roster.publishedUntil;
        }

        public get newProposedDateIfProposalIsRejected(): Date | null {
            if (this.allowRosterActionsToAnyWeekDay) {
                if (this.firstShownDay === null || !this.roster.proposedUntil) {
                    return null;
                }

                let dayBeforeFirstShownDay: Date = Dates.Utils.addDays(this.firstShownDay, -1);

                if (this.roster.publishedUntil && dayBeforeFirstShownDay < this.roster.publishedUntil) {
                    dayBeforeFirstShownDay = new Date(this.roster.publishedUntil);
                }

                return dayBeforeFirstShownDay;
            } else {
                if (this.firstShownDay === null || !this.roster.proposedUntil) {
                    return null;
                }

                let sundayBeforeFirstShownDay: Date = new Date(this.firstShownDay);
                if (Dates.Utils.getDay(this.firstShownDay) !== Dates.Utils.SUNDAY) {
                    sundayBeforeFirstShownDay.setDate(sundayBeforeFirstShownDay.getDate() - Dates.Utils.getDay(sundayBeforeFirstShownDay));
                } else if (sundayBeforeFirstShownDay.getTime() === this.roster.proposedUntil.getTime()) {
                    sundayBeforeFirstShownDay.setDate(sundayBeforeFirstShownDay.getDate() - 7);
                }

                if (this.roster.publishedUntil && sundayBeforeFirstShownDay.getTime() < this.roster.publishedUntil.getTime()) {
                    sundayBeforeFirstShownDay = new Date(this.roster.publishedUntil);
                }

                return sundayBeforeFirstShownDay;
            }
        }

        public get formattedDayUntilWhichToPerformRosterActions(): string {
            if (this.dayUntilWhichToPerformRosterActions === null) {
                return '';
            }

            return Dates.Formatter.formatDate(this.dayUntilWhichToPerformRosterActions);
        }

        public get formattedRejectProposalFromDate(): string {
            if (this.newProposedDateIfProposalIsRejected === null) {
                return '';
            }

            return Dates.Formatter.formatDateLong(Dates.Utils.addDays(this.newProposedDateIfProposalIsRejected, 1));
        }

        public get formattedNextDerivedRightsUntilDate(): string {
            if (!this.roster.publishedUntil) {
                return '';
            }

            return Dates.Formatter.formatDateLong(this.roster.publishedUntil);
        }

        public get roundedShownWeeks(): number {
            return Math.round(this.roster.days.length / 7);
        }

        public get clampedShownWeeks(): number {
            return Math.max(1, Math.min(3, this.roundedShownWeeks));
        }

        public get startDate(): number {
            return Math.floor(this.roster.days[0].date.valueOf() / 1000);
        }

        public get publishOpenShiftsStartDate(): string {
            return this.roster.days[0].date.toLocaleDateString('nl-NL', {day: '2-digit', month: '2-digit', year: 'numeric'});
        }

        public get publishOpenShiftsEndDate(): string {
            const endDate: Date = Dates.Utils.addDays(this.roster.days[0].date, this.clampedShownWeeks * 7 - 1);

            return endDate.toLocaleDateString('nl-NL', {day: '2-digit', month: '2-digit', year: 'numeric'});
        }

        public get publishAllOpenShiftsLabel(): string {
            return `Alle open diensten publiceren  (${this.publishOpenShiftsStartDate} t/m ${this.publishOpenShiftsEndDate})`;
        }

        public get roosterplanningHostUrl(): string {
            return RoosterplanningUrlBuilder.getHostUrl() ?? '';
        }

        private openUnpublishedStampsWarningModal(date: Date): Promise<'CANCEL' | 'OK'> {
            /** @see GenericConfirmationModal.openDefaultConfirmationModel() */
            // @ts-expect-error TS2339
            return GenericConfirmationModal.__vfdConstructor.openDefaultConfirmationModel(
                'LET OP: Stempels niet in bewerking',
                `In de periode sinds de laatste publicatie tot nu (${Dates.Formatter.formatDateLong(this.roster.publishedUntil!)}  t/m ${Dates.Formatter.formatDateLong(date)}) zijn de stempels van aantal medewerkers nog niet in bewerking gezet, weet u zeker dat u verder wilt gaan?`,
                ModalTheme.Warn,
            );
        }

        public proposeRosterUntil(rosterId: number, date: Date | null): void {
            if (!this.canProposeRoster || date === null) {
                return;
            }

            ApiClient.instance.fetchRosterResources(this.roster.rosterId, this.roster.proposedUntil ?? this.roster.beginDate!, date)
                .then((resources: Resource[]): void => {
                    const resourcesWithUndeployedStamps: Resource[] = resources.filter((resource: Resource): boolean =>
                        !!resource.stampDeployedUntil && resource.stampDeployedUntil < date,
                    );

                    if (resourcesWithUndeployedStamps.length > 0) {
                        this.openUnpublishedStampsWarningModal(date).then((value: 'CANCEL' | 'OK'): void => {
                            if (value !== 'OK') {
                                return;
                            }
                            this.confirmAndProposeRoster(rosterId, date);
                        });
                    } else {
                        this.confirmAndProposeRoster(rosterId, date);
                    }
                })
                /** @see GlobalErrorModal.globalErrorHandler() */
                // @ts-expect-error TS2339
                .catch(GlobalErrorModal.__vfdConstructor.globalErrorHandler);
        }

        private confirmAndProposeRoster(rosterId: number, date: Date): void {
            /** @see GenericConfirmationModal.openDefaultConfirmationModel() */
            // @ts-expect-error TS2339
            GenericConfirmationModal.__vfdConstructor.openDefaultConfirmationModel(
                'Rooster voorstellen',
                `Rooster voorstellen t/m ${Dates.Formatter.formatDateLong(date)}? Deze actie kan niet ongedaan worden gemaakt`,
            ).then((value: 'CANCEL' | 'OK'): void => {
                if (value !== 'OK') {
                    return;
                }

                ApiClient.instance.setRosterProposedUntil(rosterId, date)
                    .then((): void => {
                        this.$emit('rosterProposed', date);
                        /** @see Notify.createNotification() */
                        // @ts-expect-error TS2339
                        Notify.__vfdConstructor.createNotification({text: `Rooster voorgesteld t/m ${Dates.Formatter.formatDate(date)}.`, type: 'success'});
                    })
                    .catch((): void => {
                        /** @see Notify.createNotification() */
                        // @ts-expect-error TS2339
                        Notify.__vfdConstructor.createNotification({text: 'Rooster kon niet worden voorgesteld.', type: 'error'});
                    });
            });
        }

        public declineProposedRoster(rosterId: number): void {
            if (!this.canPublishOrDeclineProposedRoster || !this.newProposedDateIfProposalIsRejected) {
                return;
            }

            /** @see GenericConfirmationModal.openDefaultConfirmationModel() */
            // @ts-expect-error TS2339
            GenericConfirmationModal.__vfdConstructor.openDefaultConfirmationModel(
                'Roostervoorstel afwijzen',
                `Weet u zeker dat u dit rooster wilt afwijzen vanaf ${this.formattedRejectProposalFromDate}? Deze actie kan niet ongedaan worden gemaakt`,
            ).then((value: 'CANCEL' | 'OK'): void => {
                if (value !== 'OK') {
                    return;
                }

                ApiClient.instance.setRosterProposedUntil(rosterId, this.newProposedDateIfProposalIsRejected!)
                    .then((): void => {
                        /** @see Notify.createNotification() */
                        // @ts-expect-error TS2339
                        Notify.__vfdConstructor.createNotification({text: `Voorgesteld rooster afgewezen vanaf ${this.formattedRejectProposalFromDate}.`, type: 'success'});
                        this.$emit('rosterProposed', this.newProposedDateIfProposalIsRejected!);
                    })
                    .catch((e: Error): void => {
                        console.warn(e);
                        /** @see Notify.createNotification() */
                        // @ts-expect-error TS2339
                        Notify.__vfdConstructor.createNotification({text: 'Rooster voorstel kon niet worden afgewezen.', type: 'error'});
                    });
            });
        }

        public setDeriveRightsUntil(rosterId: number, date: Date | null): void {
            if (date === null || !this.canSetDeriveRightsUntil) {
                return;
            }

            this.confirmAndPublishRoster(rosterId, date);
        }

        public publishRosterUntil(rosterId: number, date: Date | null): void {
            if (date === null || !this.canPublishOrDeclineProposedRoster) {
                return;
            }

            ApiClient.instance.fetchRosterResources(this.roster.rosterId, this.roster.publishedUntil ?? this.roster.beginDate!, date)
                .then((resources: Resource[]): void => {
                    const resourcesWithUndeployedStamps: Resource[] = resources.filter((resource: Resource): boolean =>
                        !!resource.stampDeployedUntil && resource.stampDeployedUntil < date,
                    );

                    if (resourcesWithUndeployedStamps.length > 0) {
                        this.openUnpublishedStampsWarningModal(date).then((value: 'CANCEL' | 'OK'): void => {
                            if (value !== 'OK') {
                                return;
                            }
                            this.confirmAndPublishRoster(rosterId, date);
                        });
                    } else {
                        this.confirmAndPublishRoster(rosterId, date);
                    }
                })
                /** @see GlobalErrorModal.globalErrorHandler() */
                // @ts-expect-error TS2339
                .catch(GlobalErrorModal.__vfdConstructor.globalErrorHandler);
        }

        private confirmAndPublishRoster(rosterId: number, newPublishedUntilDate: Date): void {
            /** @see PublishRosterModal.openModal() */
            // @ts-expect-error TS2339
            PublishRosterModal.__vfdConstructor.openModal(this.roster, newPublishedUntilDate, this.globals.rosterHasDeriveRightsUntilEnabled)
                .then((value: RosterPublishConfirmData): void => {
                    if (value.result !== 'OK') {
                        return;
                    }
                    if (this.roster.publishedUntil) {
                        newPublishedUntilDate = Dates.Utils.max(newPublishedUntilDate, this.roster.publishedUntil);
                    }

                    if (this.globals.rosterHasDeriveRightsUntilEnabled
                        && value.deriveRightsUntil
                        && this.roster.publishedUntil
                        && newPublishedUntilDate.getTime() === this.roster.publishedUntil.getTime()
                    ) {
                        ApiClient.instance.setRosterDeriveRightsUntilDate(rosterId, value.deriveRightsUntil)
                            .then((): void => {
                                this.$emit('rosterPublished', newPublishedUntilDate);
                                /** @see Notify.createNotification() */
                                // @ts-expect-error TS2339
                                Notify.__vfdConstructor.createNotification({text: `Rooster definitief t/m  ${Dates.Formatter.formatDate(value.deriveRightsUntil!)} (gepubliceerd t/m ${Dates.Formatter.formatDate(newPublishedUntilDate)})`, type: 'success'});
                            })
                            .catch((): void => {
                                /** @see Notify.createNotification() */
                                // @ts-expect-error TS2339
                                Notify.__vfdConstructor.createNotification({text: 'Rooster kon niet definitief worden gepubliceerd.', type: 'error'});
                            });
                    } else {
                        ApiClient.instance.setRosterPublishedUntil(rosterId, newPublishedUntilDate, value.deriveRightsUntil)
                            .then((): void => {
                                this.$emit('rosterPublished', newPublishedUntilDate);
                                let message: string = 'Rooster gepubliceerd t/m ' + Dates.Formatter.formatDate(newPublishedUntilDate);
                                if (this.globals.rosterHasDeriveRightsUntilEnabled
                                    && !!value.deriveRightsUntil
                                    && newPublishedUntilDate.getTime() !== value.deriveRightsUntil.getTime()
                                ) {
                                    message += ' en rooster definitief t/m ' + Dates.Formatter.formatDate(value.deriveRightsUntil);
                                }

                                /** @see Notify.createNotification() */
                                // @ts-expect-error TS2339
                                Notify.__vfdConstructor.createNotification({text: message, type: 'success'});
                            })
                            .catch((): void => {
                                /** @see Notify.createNotification() */
                                // @ts-expect-error TS2339
                                Notify.__vfdConstructor.createNotification({text: 'Rooster kon niet worden gepubliceerd.', type: 'error'});
                            });
                    }
                });
        }

        public deployStampsUntil(): void {
            if (!this.canSetRosterInEditMode || this.firstShownDay === null || this.dayUntilWhichToPerformRosterActions === null) {
                return;
            }

            const endDate: Date = this.dayUntilWhichToPerformRosterActions;

            /** @see Loader.setLoading() */
            // @ts-expect-error TS2339
            Loader.__vfdConstructor.setLoading('roster');
            ApiClient.instance.fetchStampDeploymentAllowedPerResource(this.roster.rosterId, this.firstShownDay, endDate)
                .then((deploymentAllowedForResourceList: Record<number, boolean>): void => {
                    /** @see Loader.setLoaded() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setLoaded('roster');
                    /** @see StampDeployModal.open() */
                    // @ts-expect-error TS2339
                    StampDeployModal.__vfdConstructor.open(deploymentAllowedForResourceList, endDate);
                })
                .catch((): void => {
                    /** @see Loader.setError() */
                    // @ts-expect-error TS2339
                    Loader.__vfdConstructor.setError('roster');
                });
        }

        public closeWishInputUntil(rosterId: number, date: Date | null): void {
            if (!this.canCloseWishInput || date === null) {
                return;
            }

            /** @see GenericConfirmationModal.openDefaultConfirmationModel() */
            // @ts-expect-error TS2339
            GenericConfirmationModal.__vfdConstructor.openDefaultConfirmationModel(
                'Wensinvoer sluiten',
                `Wensinvoer sluiten t/m ${Dates.Formatter.formatDateLong(date)}?`,
            ).then((value: 'CANCEL' | 'OK'): void => {
                if (value !== 'OK') {
                    return;
                }

                // Instantiate ApiClient
                const api: ApiClient = ApiClient.instance;
                // Fire API call to update proposedUntil on roster
                api.setRosterWishInputClosedUntil(rosterId, date)
                    .then((): void => {
                        this.$emit('reload:roster');

                        /** @see Notify.createNotification() */
                        // @ts-expect-error TS2339
                        Notify.__vfdConstructor.createNotification({text: `Wensinvoer gesloten t/m ${Dates.Formatter.formatDate(date)}.`, type: 'success'});
                    })
                    .catch((): void => {
                        /** @see Notify.createNotification() */
                        // @ts-expect-error TS2339
                        Notify.__vfdConstructor.createNotification({text: 'Wensinvoer kon niet gesloten worden.', type: 'error'});
                    });
            });
        }

        public unpublishedPartVisibleUntil(rosterId: number, date: Date | null): void {
            if (!this.canMakeRosterVisible || date === null) {
                return;
            }

            /** @see GenericConfirmationModal.openDefaultConfirmationModel() */
            // @ts-expect-error TS2339
            GenericConfirmationModal.__vfdConstructor.openDefaultConfirmationModel(
                'Niet gepubliceerd rooster tonen t/m',
                `Niet gepubliceerd rooster tonen t/m ${Dates.Formatter.formatDateLong(date)}?`,
            ).then((value: 'CANCEL' | 'OK'): void => {
                if (value !== 'OK') {
                    return;
                }

                this.setRosterUnpublishedPartVisibleUntil(rosterId, date);
            });
        }

        public undoUnpublishedPartVisibleUntil(rosterId: number, date?: Date): void {
            if (!this.canUndoRosterVisible || date === undefined) {
                return;
            }

            /** @see GenericConfirmationModal.openDefaultConfirmationModel() */
            // @ts-expect-error TS2339
            GenericConfirmationModal.__vfdConstructor.openDefaultConfirmationModel(
                'Niet gepubliceerd tonen ongedaan maken',
                `Alleen gepubliceerd rooster tonen (t/m ${Dates.Formatter.formatDateLong(date)})?`,
            ).then((value: 'CANCEL' | 'OK'): void => {
                if (value !== 'OK') {
                    return;
                }

                this.setRosterUnpublishedPartVisibleUntil(rosterId, date);
            });
        }

        public addNewRosterTitle(): void {
            if (!this.canChangeRosterTitles) {
                return;
            }

            const title: Title = {
                id: 0,
                rosterRankSetId: this.roster.rosterRankSet.id!,
                title: '',
                rankInRoster: -1,
            };

            /** @see TitleEditModal.open() */
            // @ts-expect-error TS2339
            TitleEditModal.__vfdConstructor.open(title);
        }

        public openRosterRankSetsEditor(): void {
            /** @see RosterRankSetsModal.open() */
            // @ts-expect-error TS2339
            RosterRankSetsModal.__vfdConstructor.open();
        }

        private setRosterUnpublishedPartVisibleUntil(rosterId: number, date: Date): void {
            // Instantiate ApiClient
            const api: ApiClient = ApiClient.instance;
            // Fire API call to update proposedUntil on roster
            api.setRosterUnpublishedPartVisibleUntil(rosterId, date)
                .then((): void => {
                    this.$emit('reload:roster');
                    /** @see Notify.createNotification() */
                    // @ts-expect-error TS2339
                    Notify.__vfdConstructor.createNotification({text: `Datum tonen van ongepubliceerd rooster is aangepast naar ${Dates.Formatter.formatDate(date)}.`, type: 'success'});
                })
                .catch((): void => {
                    /** @see Notify.createNotification() */
                    // @ts-expect-error TS2339
                    Notify.__vfdConstructor.createNotification({text: 'Het wijzigen van de einddatum voor het tonen van het ongepubliceerde rooster is mislukt.', type: 'error'});
                });
        }
    }
</script>

<style lang="scss" scoped>
    .list-item-link {
        color: var(--label-color);
        text-decoration: none;
    }
</style>
